<template>
    <div class="substance-list-properties-editor">
        <CandidateListStructured
            v-if="isCandidateList"
            :candidate-list="substanceList"
            :compact-view="layout === 'stacked'"
            :read-only="readOnly"
            :values="productProperties"
            :properties="propertyConfig.properties"
            :substance="substanceIdentity"
            :show-exemption="false"
            :require-concentration-range="true"
            :highlight-categories="false"
            :show-categories="!hideCandidateListCategories"
            @input="changeCandidateList"
        />

        <b-row v-else>
            <b-col
                v-for="property in propertyDefinitions"
                :key="property.type"
                cols="12"
                :lg="layout === 'stacked' ? 12 : 6"
            >
                <component
                    :is="property.name"
                    :read-only="readOnly"
                    :compact-view="layout === 'stacked'"
                    :list="substanceList"
                    :substance="substanceIdentity"
                    :config="property"
                    :value="propertyValue(property)"
                    @input="value => changeProperty(property, value)"
                />
            </b-col>
        </b-row>
    </div>
</template>

<script>
import { BCol, BRow } from 'bootstrap-vue'
import { ProductPropertyType } from '@core/domain/substance'
import { SubstanceLists } from '@/libs/substance'
import {
    AdditionalMaterial,
    ConcentrationRange,
    Exemption,
    FcmRequirementsCheckField,
    MaterialCategory,
    MaxConcentration,
    MixtureCategory,
    PropertyNumberWithUnitField,
    PropertySelectField,
    PropertyYesNoQuestionsField,
} from '@/views/product/components/product-card/composition/components'
import CandidateListStructured from './CandidateListStructured.vue'

export default {
    name: 'SubstanceListPropertiesEditor',
    components: {
        AdditionalMaterial,
        BCol,
        BRow,
        CandidateListStructured,
        ConcentrationRange,
        Exemption,
        FcmRequirementsCheckField,
        MaterialCategory,
        MaxConcentration,
        MixtureCategory,
        PropertyNumberWithUnitField,
        PropertySelectField,
        PropertyYesNoQuestionsField,
    },
    props: {
        substance: { type: Object, required: true },
        substanceList: { type: Object, required: true },
        propertyConfig: {
            type: Object,
            default: () => ({ map: { list: {} }, properties: {} }),
        },
        readOnly: { type: Boolean, default: false },
        hideCandidateListCategories: { type: Boolean, default: false },
        layout: {
            type: String,
            default: 'grid',
            validator: value => ['grid', 'stacked'].includes(value),
        },
    },
    computed: {
        substanceIdentity() {
            return this.substance.substance || {}
        },
        productProperties() {
            return Array.isArray(this.substance.properties)
                ? {}
                : (this.substance.properties || {})
        },
        isCandidateList() {
            return this.substanceList.key === SubstanceLists.CANDIDATE_LIST
        },
        propertyDefinitions() {
            const map = this.propertyConfig.map && this.propertyConfig.map.list
                ? this.propertyConfig.map.list
                : {}
            const properties = this.propertyConfig.properties || {}

            return (map[this.substanceList.id] || [])
                .map(type => {
                    const property = properties[type]
                    if (!property || !property.name) {
                        return null
                    }

                    const definition = { ...property, type }
                    if (type === ProductPropertyType.MAX_CONCENTRATION_RANGE) {
                        definition.data = this.substanceList.concentrationRanges || []
                    }
                    return definition
                })
                .filter(Boolean)
        },
    },
    methods: {
        listDto() {
            if (typeof this.substanceList.toDTO === 'function') {
                return this.substanceList.toDTO()
            }
            const {
                id, key, title, renderMode,
            } = this.substanceList
            return {
                id, key, title, renderMode,
            }
        },
        propertyValue(property) {
            if (!property.listSpecific) {
                return this.productProperties[property.type]
            }

            const values = Array.isArray(this.productProperties[property.type])
                ? this.productProperties[property.type]
                : []
            const container = values.find(item => (
                item.list && String(item.list.id) === String(this.substanceList.id)
            ))
            return container ? container.value : null
        },
        setPropertyValue(properties, property, value) {
            if (!property.listSpecific) {
                properties[property.type] = value
                return
            }

            const values = Array.isArray(properties[property.type])
                ? properties[property.type].map(item => ({ ...item }))
                : []
            const index = values.findIndex(item => (
                item.list && String(item.list.id) === String(this.substanceList.id)
            ))
            if (index >= 0) {
                values[index].value = value
            } else {
                values.push({ list: this.listDto(), value })
            }
            properties[property.type] = values
        },
        emitChange(properties) {
            this.$emit('change', this.substance, properties)
        },
        changeProperty(property, value) {
            const properties = { ...this.productProperties }
            this.setPropertyValue(properties, property, value)
            this.emitChange(properties)
        },
        changeCandidateList(values) {
            const properties = { ...this.productProperties }
            Object.keys(values).forEach(type => {
                const property = this.propertyConfig.properties[type]
                if (property) {
                    this.setPropertyValue(properties, { ...property, type }, values[type])
                }
            })
            this.emitChange(properties)
        },
    },
}
</script>

<style lang="scss" scoped>
.substance-list-properties-editor {
    ::v-deep .form-group {
        margin-bottom: 1rem;
    }
}
</style>
